import { useState, useEffect } from 'react';
import { Eye, X, Mail, Phone, MapPin, Store, Clock, User, ChefHat, Check, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { BASE_URL } from '../Api/Config';

const RestaurantApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [error, setError] = useState('');

  // Fetch applications from API
  const fetchApplications = async (page = 1, status = statusFilter) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });

      if (status && status !== 'all') {
        params.append('status', status);
      }

      // Updated URL to match your route: GET /restaurant-applications/
      const response = await fetch(`${BASE_URL}/restaurant-applications/?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`, // This should be admin token
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setApplications(data.applications);
        setPagination(data.pagination);
        setError('');
      } else {
        setError(data.message || 'Failed to fetch applications');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Fetch applications error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (applicationId, newStatus, adminNotes = '') => {
    try {
      setProcessingId(applicationId);

      // Updated URL to match your route: PATCH /restaurant-applications/:applicationId/review
      const response = await fetch(`${BASE_URL}/restaurant-applications/${applicationId}/review`, {
        method: 'PATCH', // Changed from PUT to PATCH to match your route
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`, // Use consistent token key
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: newStatus,
          adminNotes
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Update local state
        setApplications(prev =>
          prev.map(app =>
            app._id === applicationId
              ? { ...app, status: newStatus, adminNotes, reviewedAt: new Date().toISOString() }
              : app
          )
        );

        // Update selected app if it's the one being updated
        if (selectedApp && selectedApp._id === applicationId) {
          setSelectedApp(prev => ({
            ...prev,
            status: newStatus,
            adminNotes,
            reviewedAt: new Date().toISOString()
          }));
        }

        // Show success message
        alert(data.message);

        // If approved, show additional info
        if (newStatus === 'approved' && data.data) {
          alert(`Restaurant created successfully!\nRestaurant ID: ${data.data.restaurantId}\nOwner Email: ${data.data.ownerEmail}`);
        }
      } else {
        alert(data.message || `Failed to ${newStatus} application`);
      }
    } catch (err) {
      alert('Network error. Please try again.');
      console.error('Status update error:', err);
    } finally {
      setProcessingId(null);
    }
  };

  // // Function to get specific application details
  // const fetchApplicationById = async (applicationId) => {
  //   try {
  //     // URL matches your route: GET /restaurant-applications/:applicationId
  //     const response = await fetch(`${BASE_URL}/restaurant-applications/${applicationId}`, {
  //       method: 'GET',
  //       headers: {
  //         'Authorization': `Bearer ${localStorage.getItem('token')}`,
  //         'Content-Type': 'application/json',
  //       },
  //     });

  //     const data = await response.json();

  //     if (data.success) {
  //       setSelectedApp(data.application);
  //       return data.application;
  //     } else {
  //       setError(data.message || 'Failed to fetch application details');
  //       return null;
  //     }
  //   } catch (err) {
  //     setError('Network error. Please try again.');
  //     console.error('Fetch application by ID error:', err);
  //     return null;
  //   }
  // };

  // Load applications on component mount and when filters change
  useEffect(() => {
    fetchApplications(currentPage, statusFilter);
    // eslint-disable-next-line
  }, [currentPage, statusFilter]);
  

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && applications.length === 0) {
    return (
      <div className="p-4 sm:p-6">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading applications...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4 sm:mb-0">Restaurant Partner Applications</h1>

          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <button
              onClick={() => fetchApplications(currentPage, statusFilter)}
              className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <span className="text-red-800">{error}</span>
            </div>
          </div>
        )}

        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs font-semibold tracking-wider">
              <tr>
                <th className="px-4 py-3">Store Name</th>
                <th className="px-4 py-3">Cuisine</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {applications.map((app) => (
                <tr key={app._id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 font-medium">{app.name}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center">
                      <ChefHat className="w-4 h-4 mr-1 text-gray-400" />
                      {app.cuisine}
                    </div>
                  </td>
                  <td className="px-4 py-4">{app.ownerName}</td>
                  <td className="px-4 py-4">{app.phone}</td>
                  <td className="px-4 py-4">{getStatusBadge(app.status)}</td>
                  <td className="px-4 py-4 text-gray-600">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1 text-gray-400" />
                      {formatDate(app.appliedAt)}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => setSelectedApp(app)}
                        className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                      {app.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleStatusUpdate(app._id, 'approved')}
                            disabled={processingId === app._id}
                            className="text-green-600 hover:text-green-800 hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                          >
                            {processingId === app._id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                            Accept
                          </button>
                          <button
                            onClick={() => handleStatusUpdate(app._id, 'rejected')}
                            disabled={processingId === app._id}
                            className="text-red-600 hover:text-red-800 hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                          >
                            <X className="w-4 h-4" />
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile/Tablet Cards */}
        <div className="lg:hidden space-y-4">
          {applications.map((app) => (
            <div key={app._id} className="bg-gray-50 rounded-lg p-4 shadow-sm">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-semibold text-lg">{app.name}</p>
                  <p className="text-sm text-gray-600">{app.ownerName}</p>
                </div>
                {getStatusBadge(app.status)}
              </div>

              <div className="space-y-2 text-sm">
                <p className="flex items-center gap-2">
                  <ChefHat className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">Cuisine:</span> {app.cuisine}
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">Contact:</span> {app.phone}
                </p>
                <p className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">Address:</span> {app.address}
                </p>
                <p className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">Submitted:</span> {formatDate(app.appliedAt)}
                </p>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  onClick={() => setSelectedApp(app)}
                  className="text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1 text-sm"
                >
                  <Eye className="w-4 h-4" />
                  View Details
                </button>
                {app.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleStatusUpdate(app._id, 'approved')}
                      disabled={processingId === app._id}
                      className="text-green-600 hover:text-green-800 hover:underline inline-flex items-center gap-1 text-sm disabled:opacity-50"
                    >
                      {processingId === app._id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                      Accept
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(app._id, 'rejected')}
                      disabled={processingId === app._id}
                      className="text-red-600 hover:text-red-800 hover:underline inline-flex items-center gap-1 text-sm disabled:opacity-50"
                    >
                      <X className="w-4 h-4" />
                      Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            <div className="text-sm text-gray-600">
              Showing {((currentPage - 1) * 10) + 1} to {Math.min(currentPage * 10, pagination.totalApplications)} of {pagination.totalApplications} applications
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.totalPages))}
                disabled={currentPage === pagination.totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {applications.length === 0 && !loading && (
          <div className="text-center py-12">
            <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No applications found</p>
          </div>
        )}
      </div>

      {/* Detailed Modal */}
      {selectedApp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white w-full max-w-lg rounded-lg shadow-lg overflow-y-auto max-h-screen p-6 relative">
            <button
              onClick={() => setSelectedApp(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Store className="w-6 h-6" />
                {selectedApp.name}
              </h2>
              {getStatusBadge(selectedApp.status)}
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <h2 className="font-semibold text-gray-900 mb-2">Store Information</h2>
                  <div className="space-y-2 text-sm">
                    <p className="flex items-center gap-2">
                      <Store className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">Name:</span> {selectedApp.name}
                    </p>
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">Contact:</span> {selectedApp.phone}
                    </p>
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">Address:</span> {selectedApp.address}
                    </p>
                    <p className="flex items-center gap-2">
                      <ChefHat className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">Cuisine:</span> {selectedApp.cuisine}
                    </p>
                  </div>
                </div>

                <div>
                  <h2 className="font-semibold text-gray-900 mb-2">Owner Information</h2>
                  <div className="space-y-2 text-sm">
                    <p className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">Name:</span> {selectedApp.ownerName}
                    </p>
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">Contact:</span> {selectedApp.ownerPhone}
                    </p>
                    <p className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">Email:</span> {selectedApp.ownerEmail}
                    </p>
                  </div>
                </div>

                <div>
                  <h2 className="font-semibold text-gray-900 mb-2">Description</h2>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">
                    {selectedApp.description}
                  </p>
                </div>

                <div>
                  <h2 className="font-semibold text-gray-900 mb-2">Timeline</h2>
                  <div className="space-y-2 text-sm">
                    <p className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">Submitted:</span> {formatDate(selectedApp.appliedAt)}
                    </p>
                    {selectedApp.reviewedAt && (
                      <p className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">Reviewed:</span> {formatDate(selectedApp.reviewedAt)}
                      </p>
                    )}
                  </div>
                </div>

                {selectedApp.image && (
                  <div>
                    <h2 className="font-semibold text-gray-900 mb-2">Restaurant Image</h2>
                    <img
                      src={selectedApp.image}
                      alt={selectedApp.name}
                      className="w-full h-48 object-cover rounded-md border"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {selectedApp.adminNotes && (
                  <div>
                    <h2 className="font-semibold text-gray-900 mb-2">Admin Notes</h2>
                    <p className="text-sm text-gray-700 bg-yellow-50 p-3 rounded-md border border-yellow-200">
                      {selectedApp.adminNotes}
                    </p>
                  </div>
                )}

                {selectedApp.createdRestaurant && (
                  <div>
                    <h2 className="font-semibold text-gray-900 mb-2">Created Restaurant</h2>
                    <div className="space-y-2 text-sm bg-green-50 p-3 rounded-md border border-green-200">
                      <p><span className="font-medium">Restaurant ID:</span> {selectedApp.createdRestaurant._id}</p>
                      <p><span className="font-medium">Name:</span> {selectedApp.createdRestaurant.name}</p>
                      <p><span className="font-medium">Status:</span>
                        <span className="ml-1 text-green-600 font-medium">
                          {selectedApp.createdRestaurant.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </p>
                    </div>
                  </div>
                )}

                {selectedApp.createdUser && (
                  <div>
                    <h2 className="font-semibold text-gray-900 mb-2">Created Owner Account</h2>
                    <div className="space-y-2 text-sm bg-blue-50 p-3 rounded-md border border-blue-200">
                      <p><span className="font-medium">User ID:</span> {selectedApp.createdUser._id}</p>
                      <p><span className="font-medium">Name:</span> {selectedApp.createdUser.name}</p>
                      <p><span className="font-medium">Email:</span> {selectedApp.createdUser.email}</p>
                      <p><span className="font-medium">Role:</span> {selectedApp.createdUser.role}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {selectedApp.status === 'pending' && (
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                  <button
                    onClick={() => handleStatusUpdate(selectedApp._id, 'approved')}
                    disabled={processingId === selectedApp._id}
                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    {processingId === selectedApp._id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Check className="w-5 h-5" />
                    )}
                    Approve Application
                  </button>
                  <button
                    onClick={() => {
                      const notes = prompt('Reason for rejection (optional):');
                      handleStatusUpdate(selectedApp._id, 'rejected', notes || '');
                    }}
                    disabled={processingId === selectedApp._id}
                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    <X className="w-5 h-5" />
                    Reject Application
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantApplications;